import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CombinedPicker } from '../CombinedPicker/CombinedPicker';
import { DetailedPicker } from '../DetailedPicker/DetailedPicker';
import './WeekDayPicker.pcss';
import { BusinessHoursByDay } from '@mm/state/model/ChatbotModel';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import CheckBox from '../../../../../../../common/components/Checkbox';

export enum WorkHours {
  FullDay,
  Hours,
}

export const DEFAULT_START_HOUR = '09:00';
export const DEFAULT_STOP_HOUR = '17:00';
export const DAY_START = '00:00';
export const DAY_END = '24:00';

export const WEEK_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

type DayPickerButtonProps = {
  idx: number;
  day: string;
  handleToggle: () => void;
  isSelected: boolean;
  className: string;
  useCheckboxStyle?: boolean;
};

export const DayPickerButton = ({
  idx,
  day,
  handleToggle,
  isSelected,
  className,
  useCheckboxStyle,
}: DayPickerButtonProps) => {
  const label = day.slice(0, 3);
  return (
    <div className={className}>
      {useCheckboxStyle ? (
        <CheckBox
          label={label}
          onToggle={handleToggle}
          isChecked={isSelected}
          checkboxLabelTitleClassName="week-day-picker__day-checkbox"
        />
      ) : (
        <PrimaryButton
          color={isSelected ? ButtonColor.blue : ButtonColor.secondaryBlue}
          key={`day-${idx}`}
          onClick={handleToggle}
          className="week-day-picker__day--button"
        >
          {label}
        </PrimaryButton>
      )}
    </div>
  );
};

export type PickerProps = {
  daysData: BusinessHoursByDay;
  setDaysData: (daysData: BusinessHoursByDay) => void;
  useCheckboxStyle?: boolean;
};

export type WeekDayPickerProps = {
  selectedTimezone: string;
  isDailyEdit: undefined | boolean;
  setIsDailyEdit: (isDailyEdit: boolean) => void;
  useCheckboxStyle?: boolean;
};

export const WeekDayPicker = ({
  isDailyEdit,
  selectedTimezone,
  setIsDailyEdit,
  useCheckboxStyle,
}: WeekDayPickerProps) => {
  const activeChatbot = useSelector(activeChatbotSelector);

  const emptyDaysSet = new Set(Array(7).keys());

  const defaultDaysData = [...emptyDaysSet].reduce(
    (hash: BusinessHoursByDay, d) => {
      hash[d] = { startHour: DEFAULT_START_HOUR, stopHour: DEFAULT_STOP_HOUR };
      return hash;
    },
    {},
  );

  const storedDaysData = activeChatbot.businessHours?.days ?? defaultDaysData;

  const daysArray = Object.values(storedDaysData);

  const isStoredDataDetailed = !daysArray.every(
    v =>
      v.startHour === daysArray[0].startHour &&
      v.stopHour === daysArray[0].stopHour,
  );

  useEffect(() => {
    if (isDailyEdit === undefined) {
      setIsDailyEdit(isStoredDataDetailed);
    }
  }, [isStoredDataDetailed, isDailyEdit]);

  const [daysData, setDaysData] = useState(storedDaysData);

  const { update: updateChatbot } = useChatbotCRUDActions();

  useEffect(() => {
    const businessHoursUpdate = {
      timezone: selectedTimezone,
      days: daysData,
    };
    if (!isEqual(businessHoursUpdate, activeChatbot.businessHours)) {
      updateChatbot({
        id: activeChatbot.id,
        businessHours: businessHoursUpdate,
      });
    }
  }, [selectedTimezone, daysData]);

  const showDetailed =
    isDailyEdit === undefined ? isStoredDataDetailed : isDailyEdit;

  return showDetailed ? (
    <DetailedPicker
      daysData={daysData}
      setDaysData={setDaysData}
      useCheckboxStyle={useCheckboxStyle}
    />
  ) : (
    <CombinedPicker
      daysData={daysData}
      setDaysData={setDaysData}
      useCheckboxStyle={useCheckboxStyle}
    />
  );
};
